import { Button, Dialog, DialogContent, DialogTitle, Grid, TextField, Typography } from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import type { IHighlight } from "@rzymek/react-pdf-highlighter";
import { AreaHighlight, PdfHighlighter, PdfLoader } from "@rzymek/react-pdf-highlighter";
import "@rzymek/react-pdf-highlighter/dist/style.css";
import type { OCRResult, OCRValue } from "./OCRService";
import { ocrAmount, ocrCurrency, ocrDateOnly, OCRService } from "./OCRService";
import { fileToDataURL } from "./fileToDataURL";
import { dateOnlyToString, stringToDateOnly } from "./dateOnly";
import DatePicker from "../../components/datePicker/DatePicker";
import CancelButton from "../../components/cancelButton/CancelButton";
import InvoiceStatusSelect from "./InvoiceStatusSelect";
import CostCurrencySelect from "../CostCurrencySelect";
import { pasteIntoSlotProps } from "./PasteIntoAdornment";
import CostExchangeRate from "../CostExchangeRate";
import { invoiceDateToExchangeRateDate } from "./InvoiceDateToExchangeRateDate";
import Margin from "./Margin";
import type { CostCurrency, CostInvoiceStatus } from "../costApi";
import { captureException } from "@sentry/browser";
import type { Cost } from "../costTypes";
import AsyncLoadingButton from "../../components/asyncLoadingButton/AsyncLoadingButton";

export interface PdfExtractDialogResult {
  readonly date: string | undefined;
  readonly amount: number | undefined;
  readonly invoiceNumber: string | undefined;
  readonly currency: CostCurrency | undefined;
  readonly status: CostInvoiceStatus | undefined;
  readonly exchangeRate: number | undefined;
}
const defaultStatus: CostInvoiceStatus = "FOR_PAYMENT";
const defaultCurrency: CostCurrency = "EUR";

export type PdfExtractDialogCost = Pick<
  Cost,
  "currency" | "order" | "invoices" | "bookingNumber" | "totalAmount" | "customer"
>;

export function PdfExtractDialog(props: {
  file: File | undefined;
  cost: PdfExtractDialogCost | undefined;
  open: boolean;
  onClose: () => void;
  onDetect(result: PdfExtractDialogResult): void;
  onChangeCurrency(value: CostCurrency): Promise<void>;
}) {
  const { file } = props;
  useEffect(() => {
    (async () => {
      if (!file) {
        return;
      }
      setLoading(true);
      setResult(undefined);
      setDataURL(await fileToDataURL(file));
    })();
  }, [file]);
  const [dataURL, setDataURL] = useState<string>();
  const [result, setResult] = useState<OCRResult>();

  const [loading, setLoading] = useState(true);
  const [pdfSelection, setPdfSelection] = useState<OCRValue<string>>();
  const handleDetect = useCallback((result: OCRResult) => {
    setLoading(false);
    setResult(result);
  }, []);

  const reloadPdfLoader = useCallback(() => {
    const currentDataUrl = dataURL;
    setDataURL(undefined);
    setTimeout(() => setDataURL(currentDataUrl), 200);
  }, [dataURL]);

  const highlights = useMemo(
    () =>
      [result?.total, result?.invoiceNumber, result?.date, result?.currency]
        .filter(
          (it): it is NonNullable<typeof it> & { bbox: NonNullable<NonNullable<typeof it>["bbox"]> } => !!it?.bbox,
        )
        .map(({ bbox }, idx) => {
          const rect = {
            ...bbox,
            width: result?.page?.width ?? 0,
            height: result?.page?.height ?? 0,
            pageNumber: bbox.page,
          } as const;
          return {
            id: `highlight-${idx}`,
            content: {},
            comment: { text: "", emoji: "" },
            position: {
              pageNumber: bbox.page ?? 0,
              boundingRect: rect,
              rects: [rect],
              usePdfCoordinates: false,
            },
          } satisfies IHighlight;
        }),
    [result],
  );
  return (
    <Dialog open={props.open} onClose={props.onClose} fullWidth maxWidth={"lg"} style={{ overflowX: "hidden" }}>
      <DialogTitle>Podgląd</DialogTitle>
      <DialogContent style={{ height: "90vh" }}>
        {file && <OCRService file={file} onDetect={handleDetect} />}
        <Grid container spacing={1} style={{ height: "100%" }}>
          <Grid size={8} style={{ position: "relative", height: "100%" }}>
            {dataURL && (
              <PdfLoader
                url={dataURL}
                beforeLoad={<div>Loading pdf viewer...</div>}
                errorMessage={<PdfLoaderErrorMessage onReload={reloadPdfLoader} />}
                onError={captureException}
              >
                {(pdfDocument) => (
                  <PdfHighlighter
                    scrollRef={(scrollTo) => highlights[0] && scrollTo(highlights[0])}
                    pdfDocument={pdfDocument}
                    highlightTransform={(highlight) => (
                      <AreaHighlight
                        key={`${highlight.id}-area`}
                        highlight={highlight}
                        onChange={() => ({})}
                        isScrolledTo={true}
                      />
                    )}
                    onSelectionFinished={({ boundingRect }, content) => {
                      setPdfSelection({
                        value: content.text ?? "",
                        bbox: {
                          x1: boundingRect.x1,
                          y1: boundingRect.y1,
                          x2: boundingRect.x2,
                          y2: boundingRect.y2,
                          page: boundingRect.pageNumber,
                        },
                      });
                      return <></>;
                    }}
                    enableAreaSelection={() => false}
                    highlights={highlights}
                    onScrollChange={() => {}}
                  />
                )}
              </PdfLoader>
            )}
          </Grid>
          <Grid size={4}>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                if (e.currentTarget.checkValidity()) {
                  const detected: PdfExtractDialogResult = {
                    date: dateOnlyToString(result?.date?.value),
                    amount: result?.total?.value,
                    invoiceNumber: result?.invoiceNumber?.value ?? defaultCurrency,
                    currency: result?.currency?.value ?? defaultCurrency,
                    status: result?.status ?? defaultStatus,
                    exchangeRate: result?.exchangeRate,
                  };
                  props.onDetect(detected);
                  props.onClose();
                }
              }}
            >
              {props.cost && (
                <Grid container spacing={1} style={{ paddingTop: 16 }}>
                  Rezerwacja: {props.cost.bookingNumber}
                  <br />
                  Zamówienie: {props.cost.order.id.userFriendlyId}
                  <br />
                  Cena: {props.cost.totalAmount} {props.cost.currency}
                  <br />
                  Klient: {props.cost.customer.name}
                  <br />
                  Produkty:
                  <ul style={{ margin: 0 }}>
                    {props.cost.order.products.map((p) => (
                      <li key={p.id}>{p.name}</li>
                    ))}
                  </ul>
                </Grid>
              )}
              <Grid container spacing={1} style={{ paddingTop: 16 }}>
                <TextField
                  label="Numer faktury"
                  disabled={loading}
                  required
                  fullWidth
                  value={result?.invoiceNumber?.value ?? ""}
                  onChange={(e) => setResult((p) => ({ ...(p ?? {}), invoiceNumber: { value: e.target.value } }))}
                  slotProps={pasteIntoSlotProps({ loading, disabled: !pdfSelection }, () =>
                    setResult((p) => ({ ...(p ?? {}), invoiceNumber: pdfSelection! })),
                  )}
                />
                <Grid container spacing={1} style={{ width: "100%" }}>
                  <Grid size={12 - 5}>
                    <TextField
                      label="Kwota"
                      required
                      disabled={loading}
                      value={result?.total?.value ?? ""}
                      onChange={(e) => setResult((p) => ({ ...(p ?? {}), total: { value: Number(e.target.value) } }))}
                      fullWidth
                      autoFocus
                      type="number"
                      slotProps={{
                        ...pasteIntoSlotProps({ loading, disabled: !pdfSelection }, () =>
                          setResult((p) => ({ ...(p ?? {}), total: ocrAmount(pdfSelection!) })),
                        ),
                        htmlInput: {
                          step: 0.01,
                        },
                      }}
                    />
                  </Grid>
                  <Grid size={5}>
                    <CostCurrencySelect
                      disabled={loading}
                      required
                      value={result?.currency?.value ?? props.cost?.currency ?? defaultCurrency}
                      onChange={(value) => value && setResult((p) => ({ ...(p ?? {}), currency: { value } }))}
                      slotProps={pasteIntoSlotProps({ loading, disabled: !pdfSelection }, () =>
                        setResult((p) => ({
                          ...(p ?? {}),
                          currency: ocrCurrency(pdfSelection!) as OCRValue<CostCurrency>,
                        })),
                      )}
                    />
                  </Grid>
                </Grid>
                <DatePicker
                  label="Data faktury"
                  disabled={loading}
                  fullWidth
                  required
                  value={dateOnlyToString(result?.date?.value) ?? ""}
                  slotProps={pasteIntoSlotProps({ loading, disabled: !pdfSelection }, () =>
                    setResult((p) => ({ ...(p ?? {}), date: ocrDateOnly(pdfSelection!) })),
                  )}
                  onChange={(date) => setResult((p) => ({ ...(p ?? {}), date: { value: stringToDateOnly(date) } }))}
                />
                <InvoiceStatusSelect
                  value={result?.status ?? defaultStatus ?? defaultStatus}
                  onChange={(value) => setResult((p) => ({ ...(p ?? {}), status: value }))}
                />
                {result?.currency?.value && result?.currency?.value !== props.cost?.order?.currency && props.cost && (
                  <Grid size={12}>
                    <CostExchangeRate
                      sourceCurrency={result?.currency?.value}
                      targetCurrency={props.cost.order.currency}
                      date={invoiceDateToExchangeRateDate(dateOnlyToString(result?.date?.value))}
                      value={result?.exchangeRate ?? 0}
                      onChange={(exchangeRate) =>
                        setResult((p) => ({ ...(p ?? {}), exchangeRate: exchangeRate ?? undefined }))
                      }
                    />
                  </Grid>
                )}
                <Grid size={12}>
                  <Margin
                    price={props.cost?.order.price ?? null}
                    orderCurrency={props.cost?.order?.currency ?? "PLN"}
                    costCurrency={result?.currency?.value ?? defaultCurrency}
                    costInvoices={[{ amount: result?.total?.value, exchangeRate: result?.exchangeRate }].filter(
                      (it) => !!it.amount && !!it.exchangeRate,
                    )}
                  />
                </Grid>
                {result?.currency?.value && props.cost && result.currency.value !== props.cost.currency ? (
                  <Grid
                    size={12}
                    spacing={1}
                    container
                    sx={{
                      justifyContent: "flex-end",
                    }}
                  >
                    {props.cost.invoices.length === 0 ? (
                      <AsyncLoadingButton
                        variant="outlined"
                        color="warning"
                        disabled={loading}
                        onClick={() => props.onChangeCurrency(result.currency!.value)}
                      >
                        Zmień walute kosztów z {props.cost.currency} na {result.currency.value}
                      </AsyncLoadingButton>
                    ) : (
                      <Typography>
                        Uwaga: zapisane są już faktury w {props.cost.currency} ({props.cost.invoices.length})
                      </Typography>
                    )}
                    <CancelButton onClick={props.onClose} />
                  </Grid>
                ) : (
                  <Grid
                    size={12}
                    spacing={1}
                    container
                    sx={{
                      justifyContent: "flex-end",
                    }}
                  >
                    <Button variant="outlined" color="primary" type="submit" disabled={loading}>
                      OK
                    </Button>
                    <CancelButton onClick={props.onClose} />
                  </Grid>
                )}
              </Grid>
            </form>
          </Grid>
        </Grid>
      </DialogContent>
    </Dialog>
  );
}

function PdfLoaderErrorMessage(props: { error?: Error; onReload: () => void }) {
  return (
    <div>
      <div style={{ textWrap: "wrap", overflow: "auto" }}>
        <h3>Wystąpił błąd podczas ładowania podglądu PDF</h3>
        <h4>
          {props.error?.name}: {props.error?.message}
        </h4>
        <pre style={{ textWrap: "wrap", fontSize: "50%" }}>{props.error?.stack}</pre>
      </div>
      <Button variant="outlined" color="warning" style={{ marginTop: "2em" }} onClick={props.onReload}>
        Spróbuj ponownie
      </Button>
    </div>
  );
}
